import { useState, useEffect, useRef } from "react"
import SidePanelLayout from "aldoo-ra/SidePanel"
import ContentBrowser from "aldoo-ra/CMS/browsers/content-browser"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { navigate } from "vike/client/router"

const getSearchParam = () => {
  if (typeof window === "undefined") return ""
  return new URLSearchParams(window.location.search).get("search") || ""
}

const setSearchParam = (term) => {
  const url = new URL(window.location.href)
  if (term) url.searchParams.set("search", term)
  else url.searchParams.delete("search")
  window.history.replaceState({}, "", url.toString())
}

// --- Note Usage Panel ---

const NoteUsagePanel = ({ note, onClose }) => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const panelRef = useRef(null)

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true)
      try {
        const result = await GetCMSContent({
          match: {
            "content.type": "HCCategory",
            "content.notes._id": note._id,
          },
          itemsPerPage: 1000,
        })
        setCategories(result?.data || [])
      } catch (e) {
        setCategories([])
      } finally {
        setLoading(false)
      }
    }
    fetchCategories()
  }, [note._id])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose()
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  const filtered = categories.filter((cat) =>
    !search || cat.content?.name?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div
      ref={panelRef}
      className="absolute z-50 w-72 bg-admin_paper border border-admin_bordered rounded-2xl shadow-lg overflow-hidden"
      style={{ top: "100%", left: 0, marginTop: 4 }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-admin_bordered">
        <span className="text-sm font-medium text-admin_text">
          Used in {categories.length} {categories.length === 1 ? "category" : "categories"}
        </span>
        <button onClick={onClose} className="text-admin_text_dim hover:text-admin_text transition-colors">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-admin_bordered">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-admin_text_dim" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="w-full pl-7 pr-3 py-1.5 text-sm bg-admin_canvas border border-admin_bordered rounded-lg text-admin_text placeholder:text-admin_text_dim focus:outline-none focus:border-admin_primary"
          />
        </div>
      </div>

      <div className="overflow-y-auto max-h-56">
        {loading ? (
          <div className="px-4 py-3 text-sm text-admin_text_dim">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-3 text-sm text-admin_text_dim">
            {search ? "No matches" : "Not used in any category"}
          </div>
        ) : (
          filtered.map((cat) => (
            <div
              key={cat._id}
              className="px-4 py-2.5 text-sm text-admin_text hover:bg-admin_canvas cursor-pointer border-b border-admin_bordered last:border-0 transition-colors"
              onClick={() => {
                onClose()
                navigate(`/admin/help-center/categories?search=${encodeURIComponent(cat.content?.name || "")}`)
              }}
            >
              <div className="font-medium">{cat.content?.name}</div>
              <div className="text-admin_text_dim text-xs">{cat.content?.slug}</div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// --- Usage Cell ---

const UsageCell = ({ note }) => {
  const [count, setCount] = useState(null)
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const result = await GetCMSContent({
          match: {
            "content.type": "HCCategory",
            "content.notes._id": note._id,
          },
          itemsPerPage: 1000,
        })
        setCount(result?.data?.length ?? 0)
      } catch {
        setCount(0)
      }
    }
    fetchCount()
  }, [note._id])

  if (count === null) return <span className="text-admin_text_dim text-sm">—</span>

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
          count > 0
            ? "bg-admin_primary/10 text-admin_primary hover:bg-admin_primary/20 cursor-pointer"
            : "bg-admin_canvas text-admin_text_dim cursor-default"
        }`}
      >
        {count} {count === 1 ? "category" : "categories"}
      </button>
      {open && count > 0 && (
        <NoteUsagePanel note={note} onClose={() => setOpen(false)} />
      )}
    </div>
  )
}

// --- Browsers ---

const HCArticleBrowser = () => (
  <ContentBrowser
    contentType="HCArticle"
    contentTitle="HC Articles"
    addButtonText="Add"
    allowTypeSelection={false}
    allowTypeBrowsing={false}
    showPreview={false}
    searchFields="content.name"
    sort={{ createdAt: -1 }}
    initialSearchTerm={getSearchParam()}
    onSearchChange={setSearchParam}
    localUserPreferencesKey="admin.help-center.articles"
    additionalColumns={[
      {
        id: "name",
        title: "Name",
        headerClassName: "w-[55%]",
        render: (_, item) => (
          <span
            className="text-admin_text cursor-pointer hover:text-admin_primary transition-colors"
            onClick={() => item?.onEdit?.(item)}
          >
            {item?.content?.name}
          </span>
        ),
      },
      {
        id: "slug",
        title: "Slug",
        headerClassName: "w-[25%]",
        render: (_, item) => (
          <span className="text-admin_text_dim text-sm">{item?.content?.slug}</span>
        ),
      },
      {
        id: "status",
        title: "Status",
        headerClassName: "w-[10%]",
        render: (_, item) => (
          <span className="text-admin_text text-sm">{item?.content?.status || "draft"}</span>
        ),
      },
      {
        id: "rating.usefulness",
        title: "Usefulness",
        headerClassName: "w-[10%]",
        sortable: true,
        render: (_, item) => {
          const score = item?.rating?.usefulness ?? 0
          return (
            <span className={`text-sm font-medium ${score > 0 ? "text-green-600" : score < 0 ? "text-red-500" : "text-admin_text_dim"}`}>
              {score > 0 ? `+${score}` : score}
            </span>
          )
        },
      },
    ]}
  />
)

const HCCategoryBrowser = () => (
  <ContentBrowser
    contentType="HCCategory"
    contentTitle="HC Categories"
    addButtonText="Add"
    allowTypeSelection={false}
    allowTypeBrowsing={false}
    showPreview={false}
    searchFields="content.name"
    initialSearchTerm={getSearchParam()}
    onSearchChange={setSearchParam}
    localUserPreferencesKey="admin.help-center.categories"
    additionalColumns={[
      {
        id: "name",
        title: "Name",
        headerClassName: "w-[55%]",
        render: (_, item) => (
          <span
            className="text-admin_text cursor-pointer hover:text-admin_primary transition-colors"
            onClick={() => item?.onEdit?.(item)}
          >
            {item?.content?.name}
          </span>
        ),
      },
      {
        id: "slug",
        title: "Slug",
        headerClassName: "w-[25%]",
        render: (_, item) => (
          <span className="text-admin_text_dim text-sm">{item?.content?.slug}</span>
        ),
      },
      {
        id: "status",
        title: "Status",
        headerClassName: "w-[10%]",
        render: (_, item) => (
          <span className="text-admin_text text-sm">{item?.content?.status || "draft"}</span>
        ),
      },
    ]}
  />
)

const HCCategoryNoteBrowser = () => (
  <ContentBrowser
    contentType="HCCategoryNote"
    contentTitle="HC Category Notes"
    addButtonText="Add"
    allowTypeSelection={false}
    allowTypeBrowsing={false}
    showPreview={false}
    searchFields="content.id"
    sort={{ createdAt: -1 }}
    initialSearchTerm={getSearchParam()}
    onSearchChange={setSearchParam}
    localUserPreferencesKey="admin.help-center.notes"
    additionalColumns={[
      {
        id: "id",
        title: "Name",
        headerClassName: "w-[35%]",
        render: (_, item) => (
          <span
            className="text-admin_text cursor-pointer hover:text-admin_primary transition-colors"
            onClick={() => item?.onEdit?.(item)}
          >
            {item?.content?.id || "—"}
          </span>
        ),
      },
      {
        id: "renderas",
        title: "Render As",
        headerClassName: "w-[20%]",
        render: (_, item) => (
          <span className="text-admin_text_dim text-sm">{item?.content?.renderas || "—"}</span>
        ),
      },
      {
        id: "usage",
        title: "Usage",
        headerClassName: "w-[25%]",
        render: (_, item) => <UsageCell note={item} />,
      },
    ]}
  />
)

const AdminHelpCenter = () => (
  <SidePanelLayout
    menuName="AdminHelpCenterMenu"
    tabs={{
      articles: HCArticleBrowser,
      categories: HCCategoryBrowser,
      notes: HCCategoryNoteBrowser,
    }}
  />
)

export default AdminHelpCenter
